import React, { useEffect, useRef, useState } from 'react';

import { Checkbox, Fieldset } from '@flixbus/honeycomb-react';
import { Asterisk } from '../../Asterisk';
import { useVoucherFormContext } from '../../Context';
import {
  CHANNELS,
  CHANNEL_LABELS,
  Channel,
  getChannelsFromRules,
  getRulesForChannels,
} from '../../../../helpers/allowedChannels';

import styles from '../../voucher-form.module.scss';

const ERROR_KEY = 'allowedPlatforms';
const MANDATORY_ERROR = 'Select at least one allowed platform';

const VoucherFormAllowedPlatforms: React.FC<{ disabled: boolean }> = ({ disabled }) => {
  const {
    formState: {
      payload: { rules },
    },
    handleRuleChange,
    setFormError,
  } = useVoucherFormContext();

  // The checkbox selection is the source of truth: the payload is lossy (e.g. all
  // channels is sent as no rules), so it is hydrated once on mount. This relies on
  // `rules` being populated before mount, which holds for this create-only form.
  const [selected, setSelected] = useState<Set<Channel>>(
    () => new Set(getChannelsFromRules(rules)),
  );

  // Skip the reconcile on mount so hydration doesn't write the same rules back
  // (marking the form dirty). Only user-driven changes should reconcile.
  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    const { allowedPlatforms, offlineSales, noAgencySales } = getRulesForChannels(selected);
    handleRuleChange(
      { type: 'allowed_platforms', platforms: allowedPlatforms },
      allowedPlatforms.length === 0,
    );
    handleRuleChange({ type: 'offline_sales' }, !offlineSales);
    handleRuleChange({ type: 'no_agency_sales' }, !noAgencySales);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Mandatory field: at least one channel must be selected.
  useEffect(() => {
    setFormError(ERROR_KEY, disabled || selected.size > 0 ? null : MANDATORY_ERROR);
  }, [disabled, selected.size, setFormError]);

  // Clear the error when this field is unmounted (e.g. switching voucher type).
  useEffect(() => () => setFormError(ERROR_KEY, null), [setFormError]);

  const handleToggle = (channel: Channel) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(channel)) {
        next.delete(channel);
      } else {
        next.add(channel);
      }
      return next;
    });
  };

  return (
    <>
      <Fieldset
        legend={
          <>
            Allowed platforms <Asterisk />
          </>
        }
        horizontal
        extraClasses={styles.choiceWrapper}
      >
        {CHANNELS.map(value => (
          <Checkbox
            key={value}
            id={`allowed-platform-${value}`}
            label={CHANNEL_LABELS[value]}
            value={value}
            checked={selected.has(value)}
            disabled={disabled}
            onChange={() => handleToggle(value)}
          />
        ))}
      </Fieldset>
    </>
  );
};

export default VoucherFormAllowedPlatforms;
